import React, { useEffect, useMemo, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';

function getNativePlatform() {
  if (typeof window === 'undefined') return null;
  if (typeof window.Capacitor?.isNativePlatform !== 'function') return null;
  if (!window.Capacitor.isNativePlatform()) return null;
  return String(window.Capacitor.getPlatform?.() || '').toLowerCase() || null;
}

function parseVersion(version) {
  return String(version || '')
    .trim()
    .split(/[.-]/)
    .map((part) => {
      const match = String(part).match(/^\d+/);
      return match ? Number(match[0]) : 0;
    });
}

function compareVersions(left, right) {
  const leftParts = parseVersion(left);
  const rightParts = parseVersion(right);
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftValue = leftParts[index] || 0;
    const rightValue = rightParts[index] || 0;
    if (leftValue > rightValue) return 1;
    if (leftValue < rightValue) return -1;
  }

  return 0;
}

function findLatestActiveDownload(downloads, platform) {
  return (downloads || [])
    .filter((download) => download?.is_active && download?.platform === platform && download?.version)
    .sort((left, right) => compareVersions(right.version, left.version))[0] || null;
}

async function openUpdateUrl(url) {
  const targetUrl = String(url || '').trim();
  if (!targetUrl) return;

  try {
    await Browser.open({ url: targetUrl });
  } catch (_error) {
    window.location.href = targetUrl;
  }
}

export default function AppUpdateGate({ children }) {
  const [state, setState] = useState({
    loading: true,
    updateRequired: false,
    currentVersion: '',
    latestDownload: null,
    platform: null,
  });

  const platform = useMemo(() => getNativePlatform(), []);

  const checkForRequiredUpdate = async () => {
    if (!platform || !['android', 'ios'].includes(platform)) {
      setState((current) => ({ ...current, loading: false, updateRequired: false, platform }));
      return;
    }

    try {
      setState((current) => ({ ...current, loading: true, platform }));
      const [appInfo, downloads] = await Promise.all([
        CapacitorApp.getInfo(),
        base44.entities.AppDownload.list('-created_date', 2000),
      ]);

      const latestDownload = findLatestActiveDownload(downloads, platform);
      const currentVersion = String(appInfo?.version || '').trim();
      const latestVersion = String(latestDownload?.version || '').trim();
      const updateRequired = Boolean(currentVersion && latestVersion && compareVersions(currentVersion, latestVersion) < 0);

      setState({
        loading: false,
        updateRequired,
        currentVersion,
        latestDownload,
        platform,
      });
    } catch (error) {
      console.error('Erro ao verificar atualização obrigatória:', error);
      setState((current) => ({ ...current, loading: false, updateRequired: false, platform }));
    }
  };

  useEffect(() => {
    checkForRequiredUpdate();
  }, [platform]);

  if (state.loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-950">
        <div className="w-8 h-8 border-4 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!state.updateRequired) {
    return children;
  }

  const latestVersion = state.latestDownload?.version || '';
  const downloadUrl = state.latestDownload?.download_url || '';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950 px-5">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
          <AlertTriangle className="h-7 w-7 text-red-600" />
        </div>

        <h1 className="text-xl font-bold text-slate-950">Atualização obrigatória</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          Existe uma nova versão do aplicativo MBV. Atualize para continuar acessando o app.
        </p>

        <div className="mt-5 rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <div>Versão instalada: {state.currentVersion || '-'}</div>
          <div>Nova versão: {latestVersion || '-'}</div>
        </div>

        <div className="mt-6 space-y-3">
          {downloadUrl ? (
            <Button className="w-full bg-red-600 hover:bg-red-700" onClick={() => openUpdateUrl(downloadUrl)}>
              <Download className="h-4 w-4" />
              Atualizar aplicativo
            </Button>
          ) : (
            <p className="text-sm text-slate-600">Link de atualização não configurado.</p>
          )}

          <Button variant="outline" className="w-full" onClick={checkForRequiredUpdate}>
            <RefreshCw className="h-4 w-4" />
            Verificar novamente
          </Button>
        </div>
      </div>
    </div>
  );
}
